import { getAvailableFonts } from "@remotion/google-fonts";
import {
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
  Fragment,
} from "react";
import { useStorageUpload } from "../../services/storage";
import { NotificationContext } from "../../context/notification-context";
import useRequest from "../../api/useRequest";
import { readFileAsArrayBuffer, getFontInfo } from "../../utils/font";
import { CustomFontRequestFactory } from "../../api/requestFactory";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

export default function FontPicker({
  onSelect,
  defaultValue,
  customFonts,
  setCustomFonts,
  addCustomFontOverride,
}) {
  const { requestMaker } = useRequest();
  const { uploadFont } = useStorageUpload();
  const [fonts, setFonts] = useState({ google: [], custom: [] });
  const [selectedFont, setSelectedFont] = useState(defaultValue || "");
  const [searchQuery, setSearchQuery] = useState("");
  const { showNotification } = useContext(NotificationContext);
  const [currentTab, setCurrentTab] = useState("google");
  const fileInputRef = useRef(null);
  const [isFontUploading, setIsFontUploading] = useState(false);
  const triggerRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const activeTabClass =
    "flex-1 w-full shadow-sm bg-white p-1 justify-center text-sm font-semibold text-gray-900 text-center rounded-md whitespace-nowrap";
  const inactiveTabClass =
    "flex-1 w-full items-center p-1 justify-center text-sm font-semibold text-gray-500 text-center rounded-md whitespace-nowrap";

  useEffect(() => {
    setSelectedFont(defaultValue || "");
  }, [defaultValue]);

  useEffect(() => {
    setFonts((prevFonts) => ({
      ...prevFonts,
      google: getAvailableFonts(),
      custom: customFonts || [],
    }));
  }, [customFonts]);

  useEffect(() => {
    fonts.custom.forEach((customFont) => {
      if (!customFont.fontUrl) {
        console.error("Font URL is missing for:", customFont.fontFamily);
        return;
      }

      const font = new FontFace(
        customFont.fontFamily,
        `url('${customFont.fontUrl}')`
      );

      font
        .load()
        .then(() => {
          document.fonts.add(font);
        })
        .catch((err) => {
          console.error("Error loading font:", customFont.fontFamily, err);
        });
    });
  }, [fonts.custom]);

  const handleUploadClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    fileInputRef.current.click();
  };

  const filteredFonts = useCallback(() => {
    const googleResults = fonts.google.filter((font) =>
      font.fontFamily.toLowerCase().includes(searchQuery.toLowerCase())
    );
    const customResults = fonts.custom.filter((font) =>
      font.fontFamily.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return { google: googleResults, custom: customResults };
  }, [fonts, searchQuery]);

  useEffect(() => {
    const { google: googleResults, custom: customResults } = filteredFonts();
    if (googleResults.length === 0 && customResults.length > 0) {
      setCurrentTab("custom");
    } else if (customResults.length === 0 && googleResults.length > 0) {
      setCurrentTab("google");
    }
  }, [filteredFonts]);

  const handleCustomFontUpload = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const file = event.target.files[0];
    if (!file) return;

    try {
      setIsFontUploading(true);
      const arrayBuffer = await readFileAsArrayBuffer(file);
      const { fontName, fontFormat } = getFontInfo(arrayBuffer, file.name);
      const uploadedFont = await uploadFont(file, fontName, fontFormat);

      const fontPayload = {
        name: fontName,
        media_id: uploadedFont.media_item?.id,
      };
      const addFontRequest = addCustomFontOverride
        ? addCustomFontOverride(fontPayload)
        : CustomFontRequestFactory.createAddUserCustomFontRequest(fontPayload);
      const response = await requestMaker(addFontRequest);

      const customFont = {
        fontFamily: response.custom_font.name,
        fontUrl: response.custom_font.font_url,
        created_at: response.custom_font.created_at,
      };

      setFonts((prevFonts) => ({
        ...prevFonts,
        custom: [...prevFonts.custom, customFont],
      }));

      setCustomFonts((prevCustomFonts) => [...prevCustomFonts, customFont]);

      showNotification(
        {
          title: "Custom Font Uploaded",
          content: `${file.name} has been uploaded successfully`,
        },
        "success"
      );
    } catch (error) {
      console.error("Font upload error:", error);
      showNotification(
        {
          title: "Font Upload Failed",
          content: "There was an error uploading your font. Please try again.",
        },
        "error"
      );
    } finally {
      setIsFontUploading(false);
    }
  };

  const handleTabClick = (tab) => (e) => {
    e.stopPropagation();
    setCurrentTab(tab);
  };

  const handleSelectFont = (font) => {
    onSelect(font.fontFamily);
    setSelectedFont(font.fontFamily);
    setIsDropdownOpen(false);
  };

  const { google: filteredGoogleFonts, custom: filteredCustomFonts } =
    filteredFonts();

  const renderFontList = (fonts, isGoogleFonts) => {
    if (fonts.length === 0) {
      return (
        <div
          className={`flex flex-col justify-center w-full items-center text-center px-4 text-sm text-gray-500 py-4 ${
            isGoogleFonts ? "h-[220px]" : "h-[150px]"
          }`}
        >
          <span className="whitespace-normal break-words text-wrap">
            {isGoogleFonts
              ? "No matching fonts found. Try a different search term."
              : "No matching custom fonts found. Upload fonts from your device to add them to your workspace."}
          </span>
        </div>
      );
    }

    return (
      <div
        className={`overflow-y-auto ${
          isGoogleFonts ? "h-[220px]" : "h-[150px]"
        } flex flex-col gap-1 px-1`}
      >
        {fonts.map((font, idx) => (
          <div
            key={idx}
            data-value={font.fontFamily}
            onClick={() => handleSelectFont(font)}
            className="bg-white border border-white hover:border-gray-200 cursor-pointer rounded-md py-0.5 px-2"
          >
            {isGoogleFonts ? (
              <img
                src={`https://ckfwvufqcepsjlnnrzqi.supabase.co/storage/v1/object/public/company/font-images/${font.fontFamily}.png`}
                alt={font.fontFamily}
                className="w-fit h-[40px] scale-y-110"
                loading="lazy"
              />
            ) : (
              <div
                style={{
                  fontFamily: `'${font.fontFamily}', sans-serif`,
                  fontSize: "14px",
                  lineHeight: "40px",
                  height: "40px",
                }}
                className="text-base font-medium p-0.5 overflow-hidden"
              >
                {font.fontFamily}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <DropdownMenu.Root open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
      <DropdownMenu.Trigger
        ref={triggerRef}
        id="font"
        name="font"
        onClick={() => setIsDropdownOpen((prev) => !prev)}
        className="flex justify-between px-2 items-center w-full outline-none relative py-2 text-gray-900 border-0 shadow-sm rounded-xl ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6 caret-blue-500"
      >
        {selectedFont ? <p>{selectedFont}</p> : <p>Select font family</p>}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-gray-500"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          onClick={(e) => e.stopPropagation()}
          side="bottom"
          align="start"
          className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-lg mt-1 mb-3 z-50"
          style={{
            width: triggerRef.current
              ? `${triggerRef.current.offsetWidth}px`
              : "100%",
          }}
        >
          <div className="outline-none flex flex-col gap-2 p-2">
            <input
              className="resize-none block w-full px-3 py-2 text-gray-900 border-0 shadow-sm rounded-lg ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6 caret-blue-500 focus:outline-none"
              onClick={(e) => e.stopPropagation()}
              placeholder="Search fonts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="flex bg-gray-100 items-center justify-center rounded-md w-full p-1">
              <button
                onClick={handleTabClick("google")}
                title=""
                className={
                  currentTab === "google" ? activeTabClass : inactiveTabClass
                }
              >
                Google
              </button>
              <button
                onClick={handleTabClick("custom")}
                className={
                  currentTab === "custom" ? activeTabClass : inactiveTabClass
                }
              >
                Custom
              </button>
            </div>
          </div>
          <div className="p-1 outline-none">
            {currentTab === "google" &&
              renderFontList(filteredGoogleFonts, true)}
            {currentTab === "custom" &&
              renderFontList(filteredCustomFonts, false)}
          </div>
          {currentTab === "custom" && (
            <div
              onSelect={(e) => e.preventDefault()}
              className="p-2 border-t outline-none"
            >
              <input
                ref={fileInputRef}
                disabled={isFontUploading}
                type="file"
                accept=".ttf,.otf,.woff,.woff2"
                onChange={handleCustomFontUpload}
                className="hidden"
              />
              <button
                disabled={isFontUploading}
                onClick={handleUploadClick}
                className="inline-flex w-full items-center justify-center gap-2 transition-all duration-150 rounded-full px-3 py-2 sm:px-4 sm:py-2.5 text-sm font-semibold hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 bg-blue-500 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isFontUploading ? (
                  <span>Uploading...</span>
                ) : (
                  <Fragment>
                    <svg
                      width="15"
                      height="14"
                      viewBox="0 0 15 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M7.5 0C7.76522 0 8.01957 0.105357 8.20711 0.292893C8.39464 0.48043 8.5 0.734784 8.5 1V6H13.5C13.7652 6 14.0196 6.10536 14.2071 6.29289C14.3946 6.48043 14.5 6.73478 14.5 7C14.5 7.26522 14.3946 7.51957 14.2071 7.70711C14.0196 7.89464 13.7652 8 13.5 8H8.5V13C8.5 13.2652 8.39464 13.5196 8.20711 13.7071C8.01957 13.8946 7.76522 14 7.5 14C7.23478 14 6.98043 13.8946 6.79289 13.7071C6.60536 13.5196 6.5 13.2652 6.5 13V8H1.5C1.23478 8 0.98043 7.89464 0.792893 7.70711C0.605357 7.51957 0.5 7.26522 0.5 7C0.5 6.73478 0.605357 6.48043 0.792893 6.29289C0.98043 6.10536 1.23478 6 1.5 6H6.5V1C6.5 0.734784 6.60536 0.48043 6.79289 0.292893C6.98043 0.105357 7.23478 0 7.5 0Z"
                        fill="white"
                      />
                    </svg>
                    <span>Upload</span>
                  </Fragment>
                )}
              </button>
            </div>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
